import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DocumentPanel } from "@/components/shared/DocumentPanel";
import { FileSelectConfirmBar } from "@/components/shared/FileSelectConfirm";
import { Loader2, UploadCloud, Check, Eye } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", industry: "", location: "", billingEmail: "", trn: "", tradeLicenseNumber: "", tradeLicenseExpiry: "", contactName: "", contactRole: "", contactEmail: "", contactPhone: "", currency: "AED" };
const REQUIRED = ["name", "billingEmail", "trn", "tradeLicenseNumber", "tradeLicenseExpiry", "contactName", "contactEmail"];

// Item 3 (Aug 2026 Sales dashboard scope pass, round 2) — India-specific KYC
// documents requested explicitly by the product owner for this UAE demo
// company. Uploaded through the existing /documents/upload endpoint
// (multipart, category=GENERAL — these are identity/KYC documents, not
// trade documents or Operations cost evidence, so GENERAL is the correct
// existing bucket; the "kind": "KYC" concept surfaced to the Sales Agent
// lives server-side in GET /customers/{cid}/document-checklist, which now
// also lists these 3 by name — see backend/server.py). No new taxonomy,
// no parallel upload path.
const KYC_DOCS = [
  { key: "aadhaar", name: "Aadhaar Card" },
  { key: "pan", name: "PAN Card" },
  { key: "cin", name: "CIN Certificate" },
];

export function CustomerOnboardingDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  // Step 2: once the customer record exists (an id is required by
  // /documents/upload's customerId field) show the KYC upload checklist.
  const [step, setStep] = useState("form");
  const [customer, setCustomer] = useState(null);
  const [kycStatus, setKycStatus] = useState({});
  const [kycBusyKey, setKycBusyKey] = useState(null);
  // Item 1 — a file chosen for a KYC slot is staged here (per doc key,
  // never uploaded) until the user clicks Submit in the confirm bar below.
  const [kycPending, setKycPending] = useState({});
  const [viewDoc, setViewDoc] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const fileRefs = useRef({});

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const valid = REQUIRED.every((key) => form[key].trim());

  const reset = () => { setForm(EMPTY); setStep("form"); setCustomer(null); setKycStatus({}); setKycPending({}); };

  const submit = async (event) => {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      const { data } = await api.post("/customers", form);
      await qc.invalidateQueries({ queryKey: ["customers"] });
      onCreated?.(data);
      toast.success(`Customer ${data.id} onboarded`);
      setCustomer(data);
      setStep("kyc");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Customer onboarding failed");
    } finally {
      setBusy(false);
    }
  };

  const stageKyc = (doc, file) => { if (file) setKycPending((cur) => ({ ...cur, [doc.key]: file })); };
  const cancelKyc = (doc) => setKycPending((cur) => { const n = { ...cur }; delete n[doc.key]; return n; });
  const reUploadKyc = (doc) => fileRefs.current[doc.key]?.click();
  const submitKyc = async (doc) => {
    const file = kycPending[doc.key];
    if (!file) return;
    const ok = await uploadKyc(doc, file);
    if (ok) setKycPending((cur) => { const n = { ...cur }; delete n[doc.key]; return n; });
  };

  const uploadKyc = async (doc, file) => {
    if (!customer) return;
    setKycBusyKey(doc.key);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("jobId", "auto");
    fd.append("docType", doc.name);
    fd.append("category", "GENERAL");
    fd.append("customerId", customer.id);
    try {
      const { data } = await api.post("/documents/upload", fd);
      setKycStatus((current) => ({ ...current, [doc.key]: data }));
      qc.invalidateQueries({ queryKey: ["cust-docs", customer.id] });
      qc.invalidateQueries({ queryKey: ["cust-doc-checklist", customer.id] });
      toast.success(`${doc.name} uploaded`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || `${doc.name} upload failed`);
      return false;
    } finally {
      setKycBusyKey(null);
    }
  };

  const finish = () => { onOpenChange(false); reset(); };

  const field = (key, label, type = "text", required = false) => <div><Label htmlFor={`onboard-${key}`}>{label}{required ? " *" : ""}</Label><Input id={`onboard-${key}`} type={type} value={form[key]} onChange={set(key)} className="mt-1" required={required} /></div>;

  return (
    <>
      <Dialog open={open} onOpenChange={(value) => { if (!busy) { if (!value) reset(); onOpenChange(value); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          {step === "form" ? (
            <>
              <DialogHeader><DialogTitle>Onboard customer</DialogTitle><DialogDescription>Complete the client identity, KYC and primary-contact record before creating a shipment inquiry.</DialogDescription></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">{field("name", "Legal company name", "text", true)}{field("industry", "Industry")}{field("location", "Registered location")}{field("billingEmail", "Billing email", "email", true)}{field("trn", "VAT / TRN", "text", true)}{field("tradeLicenseNumber", "Trade licence number", "text", true)}{field("tradeLicenseExpiry", "Trade licence expiry", "date", true)}</div>
                <div className="border-t border-slate-200 pt-4"><div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Primary contact</div><div className="grid gap-3 sm:grid-cols-2">{field("contactName", "Contact name", "text", true)}{field("contactRole", "Role")}{field("contactEmail", "Contact email", "email", true)}{field("contactPhone", "Phone / WhatsApp")}</div></div>
                <DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button><Button type="submit" disabled={!valid || busy} data-testid="onboard-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Continue to KYC documents</Button></DialogFooter>
              </form>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>KYC documents — {customer?.id}</DialogTitle>
                <DialogDescription>Upload the identity documents below. You can finish onboarding now and upload the rest later from this customer's profile.</DialogDescription>
              </DialogHeader>
              <div className="divide-y divide-slate-200 rounded-lg border border-slate-200" data-testid="onboard-kyc-list">
                {KYC_DOCS.map((doc) => {
                  const uploaded = kycStatus[doc.key];
                  const isBusy = kycBusyKey === doc.key;
                  const pendingFile = kycPending[doc.key];
                  return (
                    <div key={doc.key} className="px-3 py-2.5" data-testid={`onboard-kyc-${doc.key}`}>
                      <div className="flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-800">{doc.name}</div>
                          <div className="mt-0.5 flex items-center gap-1.5 text-[11px]">
                            {uploaded ? (
                              <span className="inline-flex items-center gap-1 font-semibold text-emerald-700"><Check className="h-3 w-3" />Uploaded</span>
                            ) : (
                              <span className="font-medium text-amber-600">Pending</span>
                            )}
                          </div>
                        </div>
                        <input
                          ref={(el) => { fileRefs.current[doc.key] = el; }}
                          type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
                          onChange={(e) => stageKyc(doc, e.target.files?.[0])}
                          data-testid={`onboard-kyc-input-${doc.key}`}
                        />
                        {uploaded && !pendingFile && (
                          <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setViewDoc(uploaded); setViewOpen(true); }} data-testid={`onboard-kyc-view-${doc.key}`}>
                            <Eye className="h-3.5 w-3.5 mr-1" />View
                          </Button>
                        )}
                        {!pendingFile && (
                          <Button type="button" size="sm" variant="outline" className="h-7 text-xs" disabled={isBusy} onClick={() => fileRefs.current[doc.key]?.click()} data-testid={`onboard-kyc-upload-${doc.key}`}>
                            {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5 mr-1" />}
                            {uploaded ? "Replace" : "Upload"}
                          </Button>
                        )}
                      </div>
                      {pendingFile && (
                        <FileSelectConfirmBar
                          pending={pendingFile}
                          busy={isBusy}
                          onSubmit={() => submitKyc(doc)}
                          onCancel={() => cancelKyc(doc)}
                          onReUpload={() => reUploadKyc(doc)}
                          submitLabel="Submit"
                          className="mt-2"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <DialogFooter><Button type="button" onClick={finish} data-testid="onboard-kyc-done">Done</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <DocumentPanel doc={viewDoc} open={viewOpen} onOpenChange={setViewOpen} />
    </>
  );
}
